import type { FieldDefinition } from '@servicetim/shared';
import { useFields, useSetFieldArchived } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Label,
  pagedTableProps,
  Switch,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import {
  Archive,
  ArchiveRestore,
  ArrowRightLeft,
  Check,
  ListOrdered,
  Pencil,
  Plus,
} from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';
import { formatDate } from '../common/format';
import { RowActions } from '../common/row-actions';
import { withProblemMessage } from '../common/server-errors';
import { ChangeTypeDialog } from './change-type-dialog';
import { FieldDialog } from './field-dialog';
import { FieldOptionsDialog } from './field-options-dialog';
import { FIELD_TYPE_LABELS, isSelectType, wideningTargets } from './field-type-labels';

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; field: FieldDefinition }
  | { kind: 'options'; field: FieldDefinition }
  | { kind: 'change-type'; field: FieldDefinition }
  | null;

/** Tenant-scoped definitions (`scope=tenant`); project-scoped ones live in the project tabs (G3). */
export function TenantFieldsTable() {
  const id = useId();
  const fields = useFields({ scope: 'tenant' });
  const setArchived = useSetFieldArchived();
  const confirm = useConfirm();
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);

  const rows = useMemo(
    () => (showArchived ? fields.items : fields.items.filter((f) => f.archivedAt === null)),
    [fields.items, showArchived],
  );

  const toggleArchived = useCallback(
    (field: FieldDefinition) => {
      const archive = field.archivedAt === null;
      void confirm({
        title: archive ? `Archive ${field.label}?` : `Unarchive ${field.label}?`,
        description: archive
          ? 'The field leaves create/edit forms and default views; stored values and history stay. Forms and queries referencing it start failing validation.'
          : 'The field becomes available to forms and views again.',
        confirmLabel: archive ? 'Archive' : 'Unarchive',
        destructive: archive,
        onConfirm: async () => {
          await withProblemMessage(() =>
            setArchived.mutateAsync({ id: field.id, archived: archive }),
          );
          toast.success(archive ? `${field.label} archived` : `${field.label} unarchived`);
        },
      });
    },
    [confirm, setArchived.mutateAsync],
  );

  const columns = useMemo<ColumnDef<FieldDefinition, unknown>[]>(
    () => [
      {
        accessorKey: 'key',
        header: 'Key',
        cell: ({ row }) => (
          <code className="rounded bg-surface-sunken px-1 font-mono text-xs">
            {row.original.key}
          </code>
        ),
      },
      {
        accessorKey: 'label',
        header: 'Label',
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span className="font-medium text-fg">{row.original.label}</span>
            {row.original.description ? (
              <span className="max-w-xs truncate text-fg-muted text-xs">
                {row.original.description}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant="secondary">{FIELD_TYPE_LABELS[row.original.type]}</Badge>
        ),
      },
      {
        accessorKey: 'indexed',
        header: 'Indexed',
        cell: ({ row }) =>
          row.original.indexed ? (
            <Check className="size-4 text-emerald-600 dark:text-emerald-400" aria-label="Indexed" />
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        accessorKey: 'enforceUnique',
        header: 'Unique',
        cell: ({ row }) =>
          row.original.enforceUnique ? (
            <Check className="size-4 text-emerald-600 dark:text-emerald-400" aria-label="Unique" />
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        accessorKey: 'archivedAt',
        header: 'Status',
        cell: ({ row }) =>
          row.original.archivedAt ? (
            <Badge variant="outline">archived</Badge>
          ) : (
            <Badge variant="success">active</Badge>
          ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-fg-muted">{formatDate(row.original.updatedAt)}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        size: 48,
        cell: ({ row }) => {
          const field = row.original;
          const archived = field.archivedAt !== null;
          return (
            <RowActions label={`Actions for ${field.label}`}>
              <DropdownMenuItem onSelect={() => setDialog({ kind: 'edit', field })}>
                <Pencil /> Edit
              </DropdownMenuItem>
              {isSelectType(field.type) ? (
                <DropdownMenuItem onSelect={() => setDialog({ kind: 'options', field })}>
                  <ListOrdered /> Options
                </DropdownMenuItem>
              ) : null}
              {wideningTargets(field.type).length > 0 ? (
                <DropdownMenuItem
                  disabled={archived}
                  onSelect={() => setDialog({ kind: 'change-type', field })}
                >
                  <ArrowRightLeft /> Change type
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              {archived ? (
                <DropdownMenuItem onSelect={() => toggleArchived(field)}>
                  <ArchiveRestore /> Unarchive
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem variant="destructive" onSelect={() => toggleArchived(field)}>
                  <Archive /> Archive
                </DropdownMenuItem>
              )}
            </RowActions>
          );
        },
      },
    ],
    [toggleArchived],
  );

  const paged = pagedTableProps(fields);

  return (
    <>
      <DataTable
        columns={columns}
        {...paged}
        data={paged.data ? rows : undefined}
        getRowId={(f) => f.id}
        caption="Tenant-scoped field definitions"
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id={`${id}-archived`}
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label htmlFor={`${id}-archived`}>Show archived</Label>
            </div>
            <Button className="ml-auto" onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> New field
            </Button>
          </div>
        }
        emptyTitle={fields.items.length === 0 ? 'No tenant fields yet' : 'No active fields'}
        emptyDescription={
          fields.items.length === 0
            ? 'Tenant fields are shared by every project; project-only fields live in each project.'
            : 'Every field is archived. Toggle "Show archived" to see them.'
        }
        emptyAction={
          fields.items.length === 0 ? (
            <Button onClick={() => setDialog({ kind: 'create' })}>
              <Plus /> New field
            </Button>
          ) : undefined
        }
      />
      {dialog?.kind === 'create' || dialog?.kind === 'edit' ? (
        <FieldDialog
          field={dialog.kind === 'edit' ? dialog.field : null}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'options' ? (
        <FieldOptionsDialog field={dialog.field} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'change-type' ? (
        <ChangeTypeDialog field={dialog.field} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}
